import type { ImageDisplayConfig, ProductImageConfig, ProductImageViewConfig } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { CONFIG_MODES, type ConfigMode } from './editor-state.js'

export type ImageBucket = ConfigMode

export const IMAGE_BUCKET_BY_MODE: Record<ConfigMode, ImageBucket> = {
  grid: 'grid',
  list: 'list',
  gridMobile: 'gridMobile',
  listMobile: 'listMobile',
}

const viewConfigFields = (display: ImageDisplayConfig | undefined): ProductImageViewConfig | undefined => {
  if (!display) return undefined
  const { fit, blendMode, paddingTop, paddingRight, paddingBottom, paddingLeft } = display
  const view: ProductImageViewConfig = {
    ...(fit !== undefined && { fit }),
    ...(blendMode !== undefined && { blendMode }),
    ...(paddingTop !== undefined && { paddingTop }),
    ...(paddingRight !== undefined && { paddingRight }),
    ...(paddingBottom !== undefined && { paddingBottom }),
    ...(paddingLeft !== undefined && { paddingLeft }),
  }
  return Object.keys(view).length ? view : undefined
}

const globalFields = (display: ImageDisplayConfig | undefined): Pick<ProductImageConfig, 'enableHoverImage'> => ({
  ...(display?.enableHoverImage !== undefined && { enableHoverImage: display.enableHoverImage }),
})

// Returns the FLAT per-branch image display config expected on a SimpleLayoutCfg's own
// `image` field: which branch (grid/list) this is already tells a consumer which
// view is active, so there's no separate grid/list bucket key nested inside it.
export const displayConfigForMode = (imageConfig: ProductImageConfig, mode: ConfigMode): ImageDisplayConfig => {
  const bucket = IMAGE_BUCKET_BY_MODE[mode]
  return {
    ...globalFields(imageConfig as ImageDisplayConfig),
    ...viewConfigFields(imageConfig[bucket]),
  }
}

// Rehydrates the editor's bucket-keyed imageConfig state from the flat per-branch image
// display configs found in an imported CombinedLayoutConfig.
export const imageConfigFromBranches = (branches: Partial<Record<ConfigMode, ImageDisplayConfig | undefined>>): ProductImageConfig => {
  const globals = [...CONFIG_MODES].reverse().reduce<ProductImageConfig>((acc, mode) => ({ ...acc, ...globalFields(branches[mode]) }), {})
  const views = CONFIG_MODES.reduce<ProductImageConfig>((acc, mode) => {
    const view = viewConfigFields(branches[mode])
    return view === undefined ? acc : { ...acc, [mode]: view }
  }, {})
  return { ...globals, ...views }
}

// Rehydrates the editor's bucket-keyed imageConfig state from a standalone (non-combined)
// SimpleLayoutCfg's flat `image` — the same display config applies to every mode uniformly.
export const imageConfigFromFlatDisplay = (display: ImageDisplayConfig | undefined): ProductImageConfig => {
  const view = viewConfigFields(display)
  return {
    ...globalFields(display),
    ...(view !== undefined && { grid: view, list: view }),
  }
}
